import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Tabs, Tooltip } from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";

const { TabPane } = Tabs;

type ViewerDocument = {
  documentUrl: string;
  documentType: string;
  documentLabelType: string;
  documentName?: string;
  id?: number;
};

type SupportingDocumentViewerModalProps = {
  open: boolean;
  documents: ViewerDocument[];
  clickedActiveTab?: string;
  clickedActiveDocumentUrl?: string;
  portalSubmission?: boolean;
  onCancel: () => void;
  onDownload: (fileName: string, url: string) => void;
  onDelete: (documentLabelType: string, documentUrl: string) => void;
};

const findTabKey = (
  groupedDocuments: Record<string, ViewerDocument[]>,
  tabName?: string,
) => {
  if (!tabName) return undefined;
  if (groupedDocuments[tabName]) return tabName;
  return Object.keys(groupedDocuments).find(
    (key) => key.toUpperCase() === tabName.toUpperCase(),
  );
};

const SupportingDocumentViewerModal: React.FC<
  SupportingDocumentViewerModalProps
> = ({
  open,
  documents,
  clickedActiveTab,
  clickedActiveDocumentUrl,
  portalSubmission = false,
  onCancel,
  onDownload,
  onDelete,
}) => {
  const [activeTab, setActiveTab] = useState<string>();
  const [activeDocument, setActiveDocument] = useState<ViewerDocument | null>(
    null,
  );
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotationAngle, setRotationAngle] = useState(0);

  const groupedDocuments = useMemo(
    () =>
      documents?.reduce(
        (acc, doc) => {
          if (doc?.documentUrl) {
            const key = doc.documentLabelType || doc.documentName || "Document";
            (acc[key] ||= []).push(doc);
          }
          return acc;
        },
        {} as Record<string, ViewerDocument[]>,
      ) || {},
    [documents],
  );

  const isPdf = activeDocument?.documentUrl
    ?.toLowerCase()
    .endsWith(".pdf");

  useEffect(() => {
    if (!open) {
      setActiveTab(undefined);
      setActiveDocument(null);
      setZoomLevel(1);
      setRotationAngle(0);
      return;
    }

    const tabKeys = Object.keys(groupedDocuments);
    if (!tabKeys.length) return;

    const urlsMatch = (a?: string, b?: string) => {
      if (!a || !b) return false;
      if (a === b) return true;
      try {
        return decodeURIComponent(a) === decodeURIComponent(b);
      } catch {
        return false;
      }
    };

    // Prefer finding the clicked URL across all tabs (fixes last/new upload preview)
    let matchedDoc: ViewerDocument | undefined;
    let matchedTab: string | undefined;
    if (clickedActiveDocumentUrl) {
      for (const key of tabKeys) {
        const found = groupedDocuments[key]?.find((entry) =>
          urlsMatch(entry.documentUrl, clickedActiveDocumentUrl),
        );
        if (found) {
          matchedDoc = found;
          matchedTab = key;
          break;
        }
      }
    }

    const resolvedTab =
      matchedTab ||
      findTabKey(groupedDocuments, clickedActiveTab) ||
      tabKeys[0];
    const docsInTab = groupedDocuments[resolvedTab] || [];
    const doc = matchedDoc || docsInTab[0] || null;

    setActiveTab(resolvedTab);
    setActiveDocument(doc);
  }, [open, clickedActiveTab, clickedActiveDocumentUrl, groupedDocuments]);

  useEffect(() => {
    setZoomLevel(1);
    setRotationAngle(0);
  }, [activeDocument?.documentUrl]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setActiveDocument(groupedDocuments[key]?.[0] || null);
  };

  const handleZoomIn = () =>
    setZoomLevel((prev) => Math.min(prev + 0.1, 3));
  const handleZoomOut = () =>
    setZoomLevel((prev) => Math.max(prev - 0.1, 0.5));
  const handleRotateLeft = () => setRotationAngle((prev) => prev - 90);
  const handleRotateRight = () => setRotationAngle((prev) => prev + 90);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={4000}
      style={{ top: 20 }}
      zIndex={10050}
      wrapClassName="supporting-document-viewer-modal"
      title="Supporting Documents"
      centered={false}
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: isPdf ? "flex-end" : "space-between",
          }}
        >
          {!isPdf && activeDocument ? (
            <div>
              <Button onClick={handleZoomOut} disabled={zoomLevel <= 0.5}>
                <ZoomOutOutlined />
              </Button>
              <Button
                onClick={handleZoomIn}
                disabled={zoomLevel >= 3}
                style={{ marginLeft: 8 }}
              >
                <ZoomInOutlined />
              </Button>
              <Button onClick={handleRotateLeft} style={{ marginLeft: 8 }}>
                <RotateLeftOutlined />
              </Button>
              <Button onClick={handleRotateRight} style={{ marginLeft: 8 }}>
                <RotateRightOutlined />
              </Button>
            </div>
          ) : null}
          <Button onClick={onCancel}>Close</Button>
        </div>
      }
    >
      <Tabs type="card" activeKey={activeTab} onChange={handleTabChange}>
        {Object.entries(groupedDocuments).map(([docType, docs]) => (
          <TabPane tab={docType} key={docType}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {docs.map((doc, index) => (
                <div
                  key={doc.id ?? `${doc.documentUrl}-${index}`}
                  style={{
                    border:
                      activeDocument?.documentUrl === doc.documentUrl
                        ? "2px solid #005499"
                        : "1px solid #ccc",
                    borderRadius: 8,
                    padding: "2px 8px",
                    width: 250,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background:
                      activeDocument?.documentUrl === doc.documentUrl
                        ? "#e6f7ff"
                        : "#fff",
                  }}
                >
                  <Tooltip title={doc.documentName || doc.documentLabelType}>
                    <div style={{ fontWeight: 500 }}>
                      {(
                        doc.documentName ||
                        doc.documentLabelType ||
                        `Document ${index + 1}`
                      ).slice(0, 22)}
                    </div>
                  </Tooltip>
                  <div style={{ display: "flex", gap: 10 }}>
                    <Tooltip title="View">
                      <EyeOutlined
                        style={{ color: "#008B8B", cursor: "pointer" }}
                        onClick={() => setActiveDocument(doc)}
                      />
                    </Tooltip>
                    <Tooltip title="Download">
                      <DownloadOutlined
                        style={{ color: "#008B8B", cursor: "pointer" }}
                        onClick={() =>
                          onDownload(
                            doc.documentName || doc.documentLabelType,
                            doc.documentUrl,
                          )
                        }
                      />
                    </Tooltip>
                    {!portalSubmission ? (
                      <Tooltip title="Delete">
                        <DeleteOutlined
                          style={{ color: "#ff4d4f", cursor: "pointer" }}
                          onClick={() => {
                            if (
                              activeDocument?.documentUrl === doc.documentUrl
                            ) {
                              setActiveDocument(null);
                            }
                            onDelete(doc.documentLabelType, doc.documentUrl);
                          }}
                        />
                      </Tooltip>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </TabPane>
        ))}
      </Tabs>

      {activeDocument ? (
        <>
          <h3 style={{ marginTop: 16 }}>
            {activeDocument.documentName || activeDocument.documentLabelType}
          </h3>
          {isPdf ? (
            <iframe
              key={activeDocument.documentUrl}
              src={activeDocument.documentUrl}
              title="PDF Preview"
              width="100%"
              height="600px"
              style={{ marginTop: 12, border: "none" }}
            />
          ) : (
            <div
              style={{
                marginTop: 12,
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                overflow: "auto",
                height: "600px",
                width: "100%",
              }}
            >
              <div
                style={{
                  minWidth: `${100 * zoomLevel}%`,
                  minHeight: `${100 * zoomLevel}%`,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  key={activeDocument.documentUrl}
                  src={activeDocument.documentUrl}
                  alt="Preview"
                  style={{
                    transform: `scale(${zoomLevel}) rotate(${rotationAngle}deg)`,
                    transition: "transform 0.3s ease",
                    maxWidth: "none",
                    maxHeight: "none",
                    width: "100%",
                    height: "auto",
                  }}
                />
              </div>
            </div>
          )}
        </>
      ) : null}
    </Modal>
  );
};

export default SupportingDocumentViewerModal;
